import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { auditKeys, auditService, type AuditEntityType } from "@/api/profiles";
import { useCompanyNames } from "@/components/Profiles/ListFilters";
import { ROUTES } from "@/routes";
import formStyles from "../ProfilesForm.module.css";
import styles from "./Audit.module.css";
import { ActionPill, ActorCell, ChangedFields, WhenCell } from "./auditShared";

export type AuditPanelProps = {
  entityType: AuditEntityType;
  entityId: string;
  /** How many recent entries to show in the compact list. */
  limit?: number;
  title?: string;
};

/**
 * Compact, reverse-chronological change history for a single entity, rendered
 * as a sidebar `<section>` matching the Status / Company cards on profile edit
 * forms. Only render when editing an existing entity (an `entityId` is present).
 */
export function AuditPanel({
  entityType,
  entityId,
  limit = 5,
  title = "Change history",
}: AuditPanelProps) {
  const query = useQuery({
    queryKey: auditKeys.forEntity(entityType, entityId, limit),
    queryFn: () => auditService.list({ entityType, entityId, limit }),
    enabled: Boolean(entityId),
  });

  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;

  // Resolve firm names for the entries shown so the actor line can badge the
  // originating firm, batched in one lookup (hook is not called per row).
  const companyIds = useMemo(
    () => items.map(i => i.companyId).filter((id): id is string => Boolean(id)),
    [items],
  );
  const companyNames = useCompanyNames(companyIds);
  const viewAllHref = `${ROUTES.profiles.audit}?entityType=${encodeURIComponent(
    entityType,
  )}&entityId=${encodeURIComponent(entityId)}`;

  return (
    <section className={formStyles.section} aria-label={title}>
      <h2 className={formStyles.sectionTitle}>{title}</h2>

      {query.isPending ? (
        <p role="status" className={styles.muted}>
          Loading change history…
        </p>
      ) : query.isError ? (
        <p role="alert" className={styles.errorBanner}>
          {query.error instanceof Error ? query.error.message : "Failed to load change history"}
        </p>
      ) : items.length === 0 ? (
        <p className={styles.muted}>No recorded changes yet.</p>
      ) : (
        <>
          <ul className={styles.panelList}>
            {items.map(entry => (
              <li key={entry.id} className={styles.panelItem}>
                <div className={styles.panelItemHeader}>
                  <ActionPill action={entry.action} />
                  <WhenCell iso={entry.createdAt} />
                </div>
                <div className={styles.panelItemMeta}>
                  <ActorCell
                    actor={entry.actor}
                    companyName={entry.companyId ? companyNames[entry.companyId] : undefined}
                  />
                  <ChangedFields fields={entry.changedFields} previewCount={3} />
                </div>
              </li>
            ))}
          </ul>

          <div className={styles.panelViewAll}>
            <Link to={viewAllHref} className={styles.panelViewAllLink}>
              View all{total > items.length ? ` (${total})` : ""}
            </Link>
          </div>
        </>
      )}
    </section>
  );
}

export default AuditPanel;
